import { useState } from "react";

// Blade Rush's own brand colors (see lib/app_colors.dart) - dark navy,
// gold for coins/weapons, red for the blade/danger accent - rather than
// a borrowed palette, so this page reads as "this game" at a glance.
const palette = {
    bg: "#0B1526",
    surface: "#122036",
    raised: "#182B47",
    border: "#22375A",
    accent: "#FFC93C",       // gold - coins, weapons, premium
    accentDim: "#332708",
    accent2: "#E5484D",      // red - blade, danger, boss
    accent2Dim: "#2A1013",
    text: "#F3F6FB",
    muted: "#8FA0BC",
    faint: "#43567A",
    success: "#3ECF6E",
    successDim: "#0E2416",
    danger: "#E5484D",
    dangerDim: "#2A1013",
    warning: "#FFB547",
    warningDim: "#2A1F08",
    info: "#3D8BFF",
    infoDim: "#0E1D33",
};

const css = `
  @import url('https://fonts.googleapis.com/css2?family=Anton&family=Space+Grotesk:wght@400;500;600;700&family=Inter:wght@400;500&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  html { scroll-behavior: smooth; }

  .br-pp {
    background: ${palette.bg};
    color: ${palette.text};
    font-family: 'Inter', sans-serif;
    font-size: 15px;
    line-height: 1.7;
    min-height: 100vh;
    position: relative;
    overflow-x: hidden;
  }

  /* Concentric target rings, faint - the game's own core visual (a
     spinning dartboard), not a generic decorative texture. */
  .br-rings-bg {
    position: fixed;
    inset: 0;
    background-image:
      repeating-radial-gradient(
        circle at 50% -6%,
        ${palette.border}30 0px,
        ${palette.border}30 1.5px,
        transparent 1.5px,
        transparent 84px
      );
    pointer-events: none;
    z-index: 0;
    opacity: 0.7;
  }

  .br-glow {
    position: fixed;
    top: -140px;
    left: 50%;
    transform: translateX(-50%);
    width: 680px;
    height: 440px;
    background: radial-gradient(ellipse, ${palette.accent}1c 0%, transparent 65%);
    pointer-events: none;
    z-index: 0;
    animation: br-pulse 5s ease-in-out infinite alternate;
  }

  @keyframes br-pulse {
    from { opacity: 0.5; }
    to   { opacity: 1; }
  }

  .br-wrap {
    position: relative;
    z-index: 1;
    max-width: 760px;
    margin: 0 auto;
    padding: 0 20px 80px;
  }

  /* Hero */
  .br-hero {
    text-align: center;
    padding: 60px 0 44px;
    animation: br-fadeup .6s ease both;
  }

  .br-badge {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: ${palette.accentDim};
    border: 1px solid ${palette.accent}44;
    border-radius: 100px;
    padding: 5px 14px;
    font-family: 'Space Grotesk', sans-serif;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 1.5px;
    color: ${palette.accent};
    text-transform: uppercase;
    margin-bottom: 22px;
  }

  /* Hero title styled after the game's own in-app wordmark: bold
     condensed all-caps, tilted, with a slash of red between the words -
     a direct callback to Blade Rush's title screen rather than a plain
     heading. */
  .br-hero-title {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    transform: rotate(-2deg);
    margin-bottom: 6px;
  }

  .br-hero-title span {
    font-family: 'Anton', sans-serif;
    font-weight: 400;
    font-size: clamp(34px, 7vw, 56px);
    letter-spacing: 1px;
    line-height: 1;
    text-transform: uppercase;
  }

  .br-hero-title .w1 { color: ${palette.text}; }
  .br-hero-title .w2 { color: ${palette.accent}; }

  .br-hero-slash {
    width: 3px;
    height: 34px;
    background: ${palette.accent2};
    transform: skewX(-18deg);
    border-radius: 2px;
    box-shadow: 0 0 12px ${palette.accent2}88;
  }

  .br-hero h1 {
    font-family: 'Space Grotesk', sans-serif;
    font-size: clamp(20px, 3.4vw, 26px);
    font-weight: 600;
    color: ${palette.muted};
    letter-spacing: -.2px;
    margin: 10px 0 18px;
  }

  .br-hero-sub {
    color: ${palette.muted};
    font-size: 14px;
    max-width: 480px;
    margin: 0 auto 18px;
  }

  .br-meta {
    display: flex;
    justify-content: center;
    flex-wrap: wrap;
    gap: 8px 20px;
    color: ${palette.faint};
    font-size: 12px;
  }

  .br-meta span {
    display: flex;
    align-items: center;
    gap: 5px;
  }

  /* Divider */
  .br-divider {
    height: 1px;
    background: linear-gradient(90deg, transparent, ${palette.border}, transparent);
    margin: 36px 0;
  }

  /* Section */
  .br-section {
    background: ${palette.surface};
    border: 1px solid ${palette.border};
    border-radius: 16px;
    padding: 24px 26px 20px;
    margin-bottom: 12px;
    transition: border-color .2s;
    animation: br-fadeup .5s ease both;
  }

  .br-section:hover { border-color: ${palette.accent}30; }

  .br-section-head {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 14px;
  }

  .br-icon {
    width: 36px; height: 36px; min-width: 36px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 18px;
  }

  .br-section h2 {
    font-family: 'Space Grotesk', sans-serif;
    font-size: 15px;
    font-weight: 600;
    letter-spacing: -.1px;
  }

  .br-section p, .br-section li {
    color: ${palette.muted};
    font-size: 14px;
    line-height: 1.75;
  }

  .br-section ul {
    padding-left: 18px;
    margin-top: 8px;
  }

  .br-section li { margin-bottom: 5px; }

  .br-section p + p { margin-top: 10px; }

  .br-section a {
    color: ${palette.info};
    text-decoration: none;
    border-bottom: 1px solid ${palette.info}44;
    transition: border-color .15s;
  }

  .br-section a:hover { border-color: ${palette.info}; }

  /* callout boxes */
  .br-box {
    border-radius: 10px;
    padding: 11px 15px;
    margin-top: 14px;
    font-size: 13px;
    display: flex;
    gap: 9px;
    align-items: flex-start;
    line-height: 1.6;
  }

  .br-box-success {
    background: ${palette.successDim};
    border: 1px solid ${palette.success}33;
    color: ${palette.success}CC;
  }

  .br-box-warning {
    background: ${palette.warningDim};
    border: 1px solid ${palette.warning}33;
    color: ${palette.warning}CC;
  }

  /* Grid cards */
  .br-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px;
    margin: 14px 0 4px;
  }

  @media (max-width: 520px) { .br-grid { grid-template-columns: 1fr; } }

  .br-card {
    background: ${palette.raised};
    border: 1px solid ${palette.border};
    border-radius: 12px;
    padding: 13px 15px;
    display: flex;
    align-items: flex-start;
    gap: 11px;
  }

  .br-card-icon {
    width: 32px; height: 32px; min-width: 32px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
  }

  .br-card strong {
    font-size: 13px;
    font-weight: 600;
    color: ${palette.text};
    display: block;
    margin-bottom: 3px;
  }

  .br-card p {
    font-size: 12px;
    color: ${palette.muted};
    line-height: 1.5;
    margin: 0 !important;
  }

  /* Contact */
  .br-contact {
    background: ${palette.accentDim};
    border: 1px solid ${palette.accent}33;
    border-radius: 16px;
    padding: 32px;
    text-align: center;
    margin-top: 36px;
  }

  .br-contact h3 {
    font-family: 'Space Grotesk', sans-serif;
    font-size: 20px;
    font-weight: 700;
    margin-bottom: 8px;
  }

  .br-contact p {
    color: ${palette.muted};
    font-size: 14px;
    margin-bottom: 20px;
  }

  .br-contact-btn {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    background: ${palette.accent};
    color: #241a02;
    font-family: 'Space Grotesk', sans-serif;
    font-weight: 700;
    font-size: 14px;
    padding: 12px 24px;
    border-radius: 100px;
    text-decoration: none;
    transition: opacity .2s, transform .2s;
  }

  .br-contact-btn:hover { opacity: .87; transform: translateY(-1px); }

  /* Footer */
  .br-footer {
    text-align: center;
    padding-top: 28px;
    color: ${palette.faint};
    font-size: 12px;
  }

  @keyframes br-fadeup {
    from { opacity: 0; transform: translateY(14px); }
    to   { opacity: 1; transform: translateY(0); }
  }
`;

// ── Icon helpers ──────────────────────────────────────────────────────────
const Ico = ({ e }) => <span role="img" aria-hidden="true">{e}</span>;

function SecIcon({ bg, children }) {
    return <div className="br-icon" style={{ background: bg }}>{children}</div>;
}

function Section({ iconBg, icon, title, children, delay = 0 }) {
    return (
        <div className="br-section" style={{ animationDelay: `${delay}s` }}>
            <div className="br-section-head">
                <SecIcon bg={iconBg}><Ico e={icon} /></SecIcon>
                <h2>{title}</h2>
            </div>
            {children}
        </div>
    );
}

function SuccessBox({ children }) {
    return <div className="br-box br-box-success"><Ico e="✓" /><span>{children}</span></div>;
}

function WarnBox({ children }) {
    return <div className="br-box br-box-warning"><Ico e="⚠" /><span>{children}</span></div>;
}

function InfoCard({ icon, iconBg, title, desc }) {
    return (
        <div className="br-card">
            <div className="br-card-icon" style={{ background: iconBg }}>
                <Ico e={icon} />
            </div>
            <div>
                <strong>{title}</strong>
                <p>{desc}</p>
            </div>
        </div>
    );
}

// ── Main ──────────────────────────────────────────────────────────────────
// Replace the placeholder studio name and email below with your own
// before publishing - they're left as clearly-marked placeholders rather
// than guessed values.
const DEVELOPER_NAME = "AlphaY";
const CONTACT_EMAIL = "alphay28071994@gmail.com";

export default function BladeRushPrivacyPolicy() {
    const [year] = useState(new Date().getFullYear());

    return (
        <>
            <style>{css}</style>
            <div className="br-pp">
                <div className="br-rings-bg" />
                <div className="br-glow" />

                <div className="br-wrap">

                    {/* Hero */}
                    <header className="br-hero">
                        <div className="br-badge">🎯 Privacy Policy</div>
                        <div className="br-hero-title">
                            <span className="w1">Blade</span>
                            <span className="br-hero-slash" />
                            <span className="w2">Rush</span>
                        </div>
                        <h1>How we handle the permissions this game asks for</h1>
                        <p className="br-hero-sub">
                            — and, just as importantly, everything it doesn't do with them.
                        </p>
                        <div className="br-meta">
                            <span>🧭 Developer: {DEVELOPER_NAME}</span>
                            <span>🎮 Free game · No account</span>
                        </div>
                    </header>

                    <div className="br-divider" />

                    {/* Overview */}
                    <Section iconBg={palette.accentDim} icon="📄" title="Overview" delay={0.05}>
                        <p>
                            <strong style={{ color: palette.text }}>Blade Rush</strong> is a knife-throwing
                            arcade game: stick blades into a spinning target, dodge spikes, collect apples,
                            unlock weapons and target skins, and climb through its levels. This page explains
                            what Blade Rush can access on your device, why, and what it does with it.
                        </p>
                        <p>By installing and using Blade Rush, you agree to the practices described below.</p>
                        <SuccessBox>
                            Blade Rush has no account, no sign-in, and no analytics or tracking SDKs. Your
                            progress is kept on your device — nothing is sent to us or anyone else.
                        </SuccessBox>
                    </Section>

                    {/* Permissions */}
                    <Section iconBg={palette.infoDim} icon="🔑" title="Permissions Blade Rush uses" delay={0.10}>
                        <p>
                            Blade Rush asks for very little. Most of what it needs is only ever exercised if
                            you actively use the optional custom-target-photo feature — nothing here runs in
                            the background or without your action.
                        </p>
                        <div className="br-grid">
                            <InfoCard
                                icon="🖼️" iconBg={palette.accentDim}
                                title="Photo library"
                                desc="Only requested if you choose to set a custom photo as your target skin. You pick one image; Blade Rush never browses your library on its own or accesses any photo you didn't select."
                            />
                            <InfoCard
                                icon="💾" iconBg={palette.successDim}
                                title="Device storage"
                                desc="Used to save your game progress and, if you set one, a copy of your chosen custom target photo — kept in the app's own local storage on your device."
                            />
                            <InfoCard
                                icon="📳" iconBg="#1A1030"
                                title="Vibration"
                                desc="Vibrates when a blade sticks in the target, if you leave vibration on in Settings. Purely local device feedback — no data involved."
                            />
                            <InfoCard
                                icon="🌐" iconBg={palette.infoDim}
                                title="Internet"
                                desc="Used once, on first launch, to download the game's title font from Google Fonts. Blade Rush has no server of its own and doesn't send your data anywhere."
                            />
                        </div>
                        <WarnBox>
                            Blade Rush's package also declares standard internet access, which several of its
                            underlying libraries include by default. Beyond the one-time font download above,
                            it isn't used to send your data anywhere.
                        </WarnBox>
                    </Section>

                    {/* Custom photo callout */}
                    <Section iconBg={palette.accent2Dim} icon="📸" title="The custom target photo feature" delay={0.13}>
                        <p>
                            Blade Rush lets you optionally set a photo from your device as your target's
                            design, in place of the game's built-in artwork. A few specifics worth being
                            explicit about:
                        </p>
                        <ul>
                            <li>Choosing a photo is entirely optional and always your action — it's never triggered automatically.</li>
                            <li>Only the one image you pick is copied into the app's local storage; nothing else in your library is read or touched.</li>
                            <li>That photo is <strong style={{ color: palette.text }}>never uploaded, transmitted, or shared</strong> anywhere — it's used only to render your target in-game, on your device.</li>
                            <li>You can replace it with a different photo, or clear it, at any time from the Targets screen.</li>
                        </ul>
                    </Section>

                    {/* Data Collected */}
                    <Section iconBg={palette.successDim} icon="🗂️" title="Information we collect" delay={0.16}>
                        <p>
                            <strong style={{ color: palette.text }}>We do not collect personally identifiable information.</strong>{" "}
                            Blade Rush does not require an account, name, email, or any profile data to play.
                        </p>
                        <p>
                            Your level progress, coins, apples, unlocked weapons and target skins, equipped
                            selections, and settings are all stored{" "}
                            <strong style={{ color: palette.text }}>locally on your device only</strong>, in a
                            local preferences store. None of it is transmitted to any server, because Blade
                            Rush doesn't have one.
                        </p>
                        <SuccessBox>
                            Uninstalling Blade Rush permanently deletes all of this data, including any custom
                            target photo, from your device.
                        </SuccessBox>
                    </Section>

                    {/* Third Parties */}
                    <Section iconBg={palette.warningDim} icon="🔌" title="Third-party services" delay={0.20}>
                        <p>
                            Blade Rush keeps its list of outside dependencies short on purpose.
                        </p>
                        <div className="br-grid">
                            <InfoCard
                                icon="🔤" iconBg={palette.infoDim}
                                title="Google Fonts"
                                desc="Delivers the typeface used in the game's title on first launch. Subject to Google's own privacy policy for that one request."
                            />
                            <InfoCard
                                icon="▶️" iconBg="#0A1A10"
                                title="Google Play Services"
                                desc="Used by Android itself for app distribution and updates. Blade Rush doesn't add any tracking on top of it."
                            />
                        </div>
                        <WarnBox>
                            Blade Rush does not currently use any advertising network, analytics SDK, or crash
                            reporting service — the in-game Store screen's currency and ad-supported offers are
                            not yet connected to a live provider. If that changes, this policy will be updated
                            first.
                        </WarnBox>
                    </Section>

                    {/* Data Retention */}
                    <Section iconBg="#0A1525" icon="🕓" title="Data retention" delay={0.25}>
                        <p>
                            Everything Blade Rush stores — your progress, unlocks, settings, and any custom
                            target photo — stays on your device until you clear the app's storage or uninstall
                            it, at which point Android deletes it automatically.
                        </p>
                        <p>
                            We do not maintain any server-side database of your data, because none of it is
                            ever sent to us in the first place.
                        </p>
                    </Section>

                    {/* Children */}
                    <Section iconBg={palette.dangerDim} icon="🧒" title="Children's privacy" delay={0.30}>
                        <p>
                            Blade Rush is a general-audience arcade game and is not directed at children. We do
                            not knowingly collect personal information from children under 13 (or under 16 in
                            the EU/EEA) — in practice, this is straightforward, since Blade Rush doesn't
                            collect personal information from anyone, of any age.
                        </p>
                        <p>
                            If you believe a child has been affected in a way this policy doesn't account for,
                            please contact us at{" "}
                            <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a> and we'll look into it
                            promptly.
                        </p>
                    </Section>

                    {/* Security */}
                    <Section iconBg={palette.successDim} icon="🛡️" title="Security" delay={0.35}>
                        <p>
                            Since Blade Rush doesn't transmit your personal data anywhere, your main security
                            consideration is your device itself. We recommend keeping Android and Blade Rush
                            updated, and being deliberate about which permissions you grant it — including the
                            ones described above.
                        </p>
                    </Section>

                    {/* Changes */}
                    <Section iconBg={palette.warningDim} icon="🔄" title="Changes to this policy" delay={0.40}>
                        <p>
                            We may update this privacy policy from time to time — for example, if a rewarded-ad
                            or analytics provider is added in the future. Changes will be posted on this page
                            with an updated effective date at the top.
                        </p>
                        <p>
                            We encourage you to review this policy periodically. Continued use of Blade Rush
                            after changes are posted constitutes acceptance of the updated policy.
                        </p>
                    </Section>

                    {/* Consent */}
                    <Section iconBg={palette.successDim} icon="✅" title="Your consent" delay={0.45}>
                        <p>
                            By installing and using Blade Rush, you consent to this privacy policy. If you do
                            not agree, please discontinue use of the game, turn off any permissions you granted
                            it in Android Settings, and uninstall it.
                        </p>
                    </Section>

                    {/* Contact */}
                    <div className="br-contact">
                        <h3>Questions or concerns?</h3>
                        <p>
                            If anything here isn't clear, or you want to know more about how a specific
                            permission is used, reach out any time.
                        </p>
                        <a href={`mailto:${CONTACT_EMAIL}`} className="br-contact-btn">
                            ✉️ &nbsp;{CONTACT_EMAIL}
                        </a>
                    </div>

                    {/* Footer */}
                    <div className="br-divider" />
                    <footer className="br-footer">
                        <p>© {year} <strong style={{ color: palette.muted }}>{DEVELOPER_NAME}</strong> · Blade Rush · All rights reserved.</p>
                        <p style={{ marginTop: 5 }}>This app is not affiliated with or endorsed by Google LLC.</p>
                    </footer>

                </div>
            </div>
        </>
    );
}